// src/routes.js
import { lazy } from "react";
import { useParams, Navigate } from "react-router-dom";
import ProtectedRoute from "../pages/ProtectedRoute";
import { chakras } from "../components/emotions/chakra/constants/chakraConfig";

// Simplified lazy loading syntax
const SplashScreen = lazy(() => import("../components/auth/SplashScreen"));
const InvitationLogin = lazy(() => import("../components/auth/InvitationLogin"));
const RegistroNino = lazy(() => import("../components/auth/register/RegistroNino"));
const RegistroAdultoProfesionalMVP = lazy(() => import("../components/auth/register/RegistroAdultoProfesional"));
const DashboardAdulto = lazy(() => import("../pages/DashboardAdulto"));
const DashboardAdmin = lazy(() => import("../pages/DashboardAdmin"));
const Menu = lazy(() => import("../pages/Menu"));
const WorldMap = lazy(() => import("../pages/WorldMap"));
const Room = lazy(() => import("../pages/Room"));
const EmotionIntro = lazy(() => import("../components/emotions/EmotionIntro"));
const DiaryIntro = lazy(() => import("../components/diary/DiaryIntro"));
const FamilyTreeIntro = lazy(() => import("../components/familyTree/FamilyTreeIntro"));
const PaintEmotion = lazy(() => import("../components/emotions/PaintEmotion"));
const EmotionDetail = lazy(() => import("../components/emotions/EmotionDetail"));
const EmotionSelector = lazy(() => import("../components/emotions/EmotionSelector"));
const WhatDoIFeelInMyBody = lazy(() => import("../components/emotions/chakra/WhatDoIFeelInMyBody"));
const ChakraConfirmation = lazy(() => import("../components/emotions/chakra/ChakraConfirmation"));
const ChakraDetailScreen = lazy(() => import("../components/emotions/chakra/ChakraDetailScreen"));
const TermsAndConditions = lazy(() => import("../pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("../pages/PrivacyPolicy"));
const NotFound404 = lazy(() => import("../pages/NotFound404"));
const PiggyBankPage = lazy(() => import("../pages/PiggyBankPage"));
const ResetPasswordRequest = lazy(() => import("../pages/ResetPasswordRequest"));
const ResetPasswordConfirm = lazy(() => import("../pages/ResetPasswordConfirm"));
const AdultosVinculados = lazy(() => import("../components/child/AdultosVinculados"));
const AdultoDeConfianza = lazy(() => import("../components/child/AdultoDeConfianza"));

// Wrapper component for ChakraConfirmation to pass chakra data
function ChakraConfirmationWrapper() {
    const { chakraId } = useParams();
    const chakra = chakras.find((c) => c.id === chakraId);

    if (!chakra) {
        return <Navigate to="/what-do-i-feel" replace />;
    }

    return <ChakraConfirmation chakraId={chakraId} chakra={chakra} />;
}

export const routes = [
    {
        path: "/",
        element: <SplashScreen/>,
        public: true
    },
    {
        path: "/invitation-login",
        element: <InvitationLogin/>,
        public: true
    },
    {
        path: "/auth/register/adult",
        element: <RegistroAdultoProfesionalMVP/>,
        public: true
    },
    {
        path: "/auth/register/child",
        element: <RegistroNino/>,
        public: true
    },
    {
        path: "/dashboard",
        element: (
            <ProtectedRoute requiredRole="adult">
                <DashboardAdulto/>
            </ProtectedRoute>
        )
    },
    {
        path: "/dashboard-admin",
        element: (
            <ProtectedRoute requiredRole="admin">
                <DashboardAdmin/>
            </ProtectedRoute>
        )
    },
    {
        path: "/menu",
        element: <Menu/>,
        public: true
    },
    {
        path: "/alcancia",
        element: (
            <ProtectedRoute requiredRole="child">
                <PiggyBankPage/>
            </ProtectedRoute>
        )
    },
    {
        path: "/world-map",
        element: (
            <ProtectedRoute requiredRole="child">
                <WorldMap/>
            </ProtectedRoute>
        )
    },
    {
        path: "/room",
        element: (
            <ProtectedRoute requiredRole="child">
                <Room/>
            </ProtectedRoute>
        )
    },
    {
        path: "/emotion-intro",
        element: (
            <ProtectedRoute requiredRole="child">
                <EmotionIntro/>
            </ProtectedRoute>
        )
    },
    {
        path: "/diary-intro",
        element: (
            <ProtectedRoute requiredRole="child">
                <DiaryIntro/>
            </ProtectedRoute>
        )
    },
    {
        path: "/family-tree",
        element: (
            <ProtectedRoute requiredRole="child">
                <FamilyTreeIntro/>
            </ProtectedRoute>
        )
    },
    {
        path: "/paint-emotion",
        element: (
            <ProtectedRoute requiredRole="child">
                <PaintEmotion/>
            </ProtectedRoute>
        )
    },
    {
        path: "/detalle/:emotion",
        element: (
            <ProtectedRoute requiredRole="child">
                <EmotionDetail/>
            </ProtectedRoute>
        )
    },
    {
        path: "/emotion-selector",
        element: (
            <ProtectedRoute requiredRole="child">
                <EmotionSelector/>
            </ProtectedRoute>
        )
    },
    {
        path: "/what-do-i-feel",
        element: (
            <ProtectedRoute requiredRole="child">
                <WhatDoIFeelInMyBody/>
            </ProtectedRoute>
        )
    },
    {
        path: "/what-do-i-feel/confirm/:chakraId",
        element: (
            <ProtectedRoute requiredRole="child">
                <ChakraConfirmationWrapper/>
            </ProtectedRoute>
        )
    },
    {
        path: "/what-do-i-feel-in-my-body/:chakraId",
        element: (
            <ProtectedRoute requiredRole="child">
                <ChakraDetailScreen/>
            </ProtectedRoute>
        )
    },
    {
        path: "/adultos-vinculados",
        element: (
            <ProtectedRoute requiredRole="child">
                <AdultosVinculados/>
            </ProtectedRoute>
        )
    },
    {
        path: "/adulto-de-confianza",
        element: (
            <ProtectedRoute requiredRole="child">
                <AdultoDeConfianza/>
            </ProtectedRoute>
        )
    },
    {
        path: "/reset-password-request",
        element: <ResetPasswordRequest/>,
        public: true
    },
    {
        path: "/reset-password",
        element: <ResetPasswordConfirm/>,
        public: true
    },
    {
        path: "/terms",
        element: <TermsAndConditions/>,
        public: true
    },
    {
        path: "/privacy",
        element: <PrivacyPolicy/>,
        public: true
    },
    {
        path: "*",
        element: <NotFound404/>,
        public: true
    }
];
